import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';

import { CompetitionDetailsGameplayScoringStrategy_NBALeaderboard } from './__generated__/index.graphql';

const ImportantText = styled.span`
  color: var(--c-red-800);
`;
const messages = defineMessages({
  defaultStrategy: {
    id: 'CompetitionDetails.Gameplay.ScoringStrategy.default',
    defaultMessage:
      'Points, assists, rebounds, blocks, steals, turnovers, 3-pointers made, double-double, triple-double',
  },
  nbaOffensive: {
    id: 'CompetitionDetails.Gameplay.ScoringStrategy.nbaOffensive',
    defaultMessage:
      'Points, assists, rebounds, 3-pointers made, double-double, triple-double',
  },
  nbaDefensive: {
    id: 'CompetitionDetails.Gameplay.ScoringStrategy.nbaDefensive',
    defaultMessage: 'Blocks, rebounds, steals',
  },
  nbaReboundAssistBoost: {
    id: 'CompetitionDetails.Gameplay.ScoringStrategy.nbaReboundAssistBoost',
    defaultMessage: 'Rebounds and assists are worth double',
  },
  nbaPointsFree: {
    id: 'CompetitionDetails.Gameplay.ScoringStrategy.nbaPointsFree',
    defaultMessage:
      '<strong>Points do not count.</strong> All other scoring categories count: assists, rebounds, blocks, steals, turnovers, 3-pointers made, double-double, triple-double.',
  },
});

type Props = {
  leaderboard?: CompetitionDetailsGameplayScoringStrategy_NBALeaderboard | null;
};

const Content = ({ leaderboard }: Props) => {
  switch (leaderboard?.scoringStrategy) {
    case 'nba_rebound_assist_boost': {
      return <FormattedMessage {...messages.nbaReboundAssistBoost} />;
    }
    case 'nba_defensive': {
      return <FormattedMessage {...messages.nbaDefensive} />;
    }
    case 'nba_offensive': {
      return <FormattedMessage {...messages.nbaOffensive} />;
    }
    case 'nba_points_free': {
      return (
        <FormattedMessage
          {...messages.nbaPointsFree}
          values={{
            strong: (chunks: ReactNode[]) => (
              <ImportantText>{chunks}</ImportantText>
            ),
          }}
        />
      );
    }
    default: {
      return <FormattedMessage {...messages.defaultStrategy} />;
    }
  }
};

export const ScoringStrategy = ({ leaderboard }: Props) => {
  return (
    <Skeleton loading={!leaderboard}>
      <Content leaderboard={leaderboard} />
    </Skeleton>
  );
};

ScoringStrategy.fragments = {
  NBALeaderboard: gql`
    fragment CompetitionDetailsGameplayScoringStrategy_NBALeaderboard on NBALeaderboard {
      slug
      scoringStrategy
    }
  ` as TypedDocumentNode<CompetitionDetailsGameplayScoringStrategy_NBALeaderboard>,
};
